import { Panel } from '@pandiafi/components'
import { Span } from '@pandiafi/design-system'
import classNames from 'classnames'
import i18next from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { PaymentsByMonth } from '../../../../../common/generated/residentApiSchemas'
import {
  getMonthInTranslationKey,
  MonthNumber,
} from '../../../../utils/dateUtils'
import { Divider } from '../../Divider'
import { hideCurrentYear, localizedMonthName } from '../../utils'
import { DifferenceTotal } from '../DifferenceTotal'
import { InvoiceTargetTables } from '../InvoiceTargetTables/InvoiceTargetTables'
import { RemittanceTable } from '../RemittanceTable'
import { TRemittance } from '../../../../../common/validations/remittance'

export type PaymentMonthVariant = 'current' | 'next' | 'future' | 'history'

interface PaymentMonthProps {
  index: number
  month: PaymentsByMonth
  remittances: TRemittance[]
  withholdings: PaymentsByMonth['withholdingsUsed']
  variant: PaymentMonthVariant
}

export const PaymentMonth: React.FC<PaymentMonthProps> = ({
  index,
  month,
  remittances,
  withholdings,
  variant,
}) => {
  const { t } = useTranslation(['payments', 'common'])
  const [year, monthNumber] = month.yearMonth.split('-')
  const monthName = localizedMonthName(monthNumber, year, i18next.language)
  const title = `${monthName} ${hideCurrentYear(year)}`.trim()
  const isCurrentMonth = variant === 'current'
  const isFutureMonth = variant === 'next' || variant === 'future'
  const showsCumulativeBalance = isCurrentMonth || isFutureMonth

  return (
    <Panel
      title={title}
      titleElement={
        isCurrentMonth ? (
          <span
            aria-label={t('payments:currentMonth')}
            role="img"
            className="ml-1 inline-block size-3 rounded-full bg-common-blue-6"
          />
        ) : null
      }
      expandable
      defaultExpanded={isCurrentMonth}
      padding="s"
      className={classNames(
        '-mx-4 tablet:-mx-6 tablet:!px-6 desktop:-mx-7 desktop:!px-7 [&_h3]:!font-bold',
        {
          'bg-common-blue-2': index % 2 === 1,
        }
      )}
    >
      <div>
        <Span bold className="mb-4 desktop:mb-6 block uppercase">
          {t('payments:invoices')}
        </Span>

        <InvoiceTargetTables
          paymentsByTarget={month.paymentsByTarget}
          openCreditNotes={month.openCreditNotes}
        />

        <Divider className="mt-6 mb-8 desktop:mb-10" />

        <Span bold className="mb-4 desktop:mb-8 block uppercase">
          {t('payments:remittances')}
        </Span>

        <RemittanceTable
          allRemittances={remittances}
          paymentOrders={month.paymentOrders}
          withholdings={withholdings}
          year={year}
          monthNumber={monthNumber}
          paymentsSums={month.paymentsByTarget.map(
            (payment) => payment.expected
          )}
        />

        {showsCumulativeBalance ? (
          <DifferenceTotal
            amount={month.monthlyDifference}
            month={t(getMonthInTranslationKey(monthNumber as MonthNumber))}
          />
        ) : null}
      </div>
    </Panel>
  )
}
